import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { SettingsSection } from 'design-system/SettingsSection';
import { SettingsItemContent, SettingsItemDescription, SettingsItemNavigate, SettingsItemTitle, } from 'design-system/SettingsItem';
import { IconFlag, IconProfile, IconHashTag, IconBoldCalendar, } from 'design-system/icons';
import useColors from 'hooks/useColors';
import { TitleModal } from 'features/borrow/components/monevo/TitleModal';
import { TITLES } from 'features/borrow/monevo/models';
import Text from 'design-system/Text';
import { registerUser } from 'actions';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useDateTimePickerDOB, { DOB_DISPLAY_FORMAT, } from 'hooks/useDateTimePickerDOB';
import { selectIsIdentityLocked, selectUser, selectUserNationalities, } from 'reducers/selectors';
import { isWeb } from '../../../../constants';
const strings = {
    sectionName: 'Personal details',
    title: 'Title',
    legalFirstName: 'Legal first name',
    legalMiddleName: 'Legal middle name',
    legalLastName: 'Legal last name',
    dob: 'Date of birth',
    countryOfCitizenship: 'Citizenships',
    emmaTag: 'Emma tag',
    defaultTitle: 'Add...',
    defaultTitleAlt: 'Choose...',
};
export const PersonalDetailsSection = () => {
    const { title: userTitle, firstName, middleName, lastName, dateOfBirth, kycStartedAt, urlHandle, } = useAppSelector(selectUser);
    const nationalities = useAppSelector(selectUserNationalities);
    const areIdentityFieldsDisabled = useAppSelector(selectIsIdentityLocked);
    const colors = useColors();
    const areFieldsDisabled = Boolean(kycStartedAt);
    const { navigateDeprecated } = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const [pendingTitle, setPendingTitle] = useState(null);
    const displayedTitle = pendingTitle ?? userTitle;
    const titleLabel = TITLES.find((t) => t.value === displayedTitle)?.label ||
        strings.defaultTitleAlt;
    const [, , , postTitle] = useFetchOld(muteErrorAndResult);
    const changeTitle = useCallback(() => {
        TitleModal.show(async (titleObj) => {
            if (titleObj.value === displayedTitle) {
                return;
            }
            setPendingTitle(titleObj.value);
            const res = await postTitle(registerUser({ title: titleObj.value }));
            if (!res) {
                setPendingTitle(null);
            }
        });
    }, [displayedTitle, postTitle]);
    const changeName = useCallback(() => {
        navigateDeprecated('ChangeDetail', { key: 'firstName' });
    }, []);
    const changeMiddleName = useCallback(() => {
        navigateDeprecated('ChangeDetail', { key: 'middleName' });
    }, []);
    const changeLastName = useCallback(() => {
        navigateDeprecated('ChangeDetail', { key: 'lastName' });
    }, []);
    const changeTag = useCallback(() => {
        navigateDeprecated('ChangeDetail', { key: 'urlHandle' });
    }, []);
    const showCountrySelectorModal = useCallback(() => {
        navigateDeprecated('MultipleCitizenshipPickerScreen');
    }, []);
    const { showDateTimePicker, dob } = useDateTimePickerDOB({
        dateOfBirth,
    }, 16);
    useEffect(() => {
        if (dob &&
            dateOfBirth &&
            dob !== format(toZonedTime(dateOfBirth, 'UTC'), 'yyyy-MM-dd')) {
            dispatch(registerUser({
                dateOfBirth: dob,
            }));
        }
    }, [dob, dateOfBirth]);
    const defaultTitle = areFieldsDisabled ? '' : strings.defaultTitle;
    return (<SettingsSection name={strings.sectionName}>
      {!!(!isWeb || userTitle) && (<SettingsItemNavigate icon={<IconProfile gradientKey="lime"/>} disabled={isWeb} description={strings.title} title={titleLabel} onPress={changeTitle} isSelected={false}/>)}

      {!!(!isWeb || firstName) && (<SettingsItemNavigate icon={<IconProfile gradientKey="ultimate"/>} disabled={isWeb || areIdentityFieldsDisabled} description={strings.legalFirstName} title={firstName || defaultTitle} onPress={changeName}/>)}

      {!!(!isWeb || middleName) &&
            !!(middleName || !areIdentityFieldsDisabled) && (<SettingsItemNavigate icon={<IconProfile gradientKey="deepPink"/>} disabled={isWeb || areIdentityFieldsDisabled} description={strings.legalMiddleName} title={middleName || defaultTitle} onPress={changeMiddleName}/>)}

      {!!(!isWeb || lastName) && (<SettingsItemNavigate icon={<IconProfile gradientKey="skyBlue"/>} disabled={isWeb || areIdentityFieldsDisabled} description={strings.legalLastName} title={lastName || defaultTitle} onPress={changeLastName}/>)}

      {!!(!isWeb || urlHandle) && (<SettingsItemNavigate icon={<IconHashTag gradientColors={colors.gradients.peach.colors}/>} disabled={isWeb} description={strings.emmaTag} title={urlHandle ? `@${urlHandle}` : defaultTitle} onPress={changeTag}/>)}

      {!!(!isWeb || dateOfBirth) && (<SettingsItemNavigate icon={<IconBoldCalendar gradientColors={colors.gradients.darkPurple.colors}/>} disabled={isWeb || areIdentityFieldsDisabled} onPress={showDateTimePicker}>
          <SettingsItemContent>
            <SettingsItemDescription>{strings.dob}</SettingsItemDescription>
            <SettingsItemTitle>
              <Text Text-16>
                {dateOfBirth
                ? format(toZonedTime(dateOfBirth, 'UTC'), DOB_DISPLAY_FORMAT)
                : defaultTitle}
              </Text>
            </SettingsItemTitle>
          </SettingsItemContent>
        </SettingsItemNavigate>)}

      {!!(!isWeb || nationalities) && (<SettingsItemNavigate icon={<IconFlag />} disabled={isWeb || (areFieldsDisabled && !!nationalities)} onPress={showCountrySelectorModal} title={nationalities || strings.defaultTitle} description={strings.countryOfCitizenship}/>)}
    </SettingsSection>);
};
